import {
  type ImgHTMLAttributes,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

type Props = ImgHTMLAttributes<HTMLImageElement>;

const ImageInternal = ({ onError, ...props }: Props) => {
  const [retryCount, setRetryCount] = useState(0);
  const [imgKey, setImgKey] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleError = useCallback<React.ReactEventHandler<HTMLImageElement>>(
    (event) => {
      if (retryCount < MAX_RETRIES) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(
          () => {
            setRetryCount((prev) => prev + 1);
            // force remount by changing key
            setImgKey((prev) => prev + 1);
          },
          RETRY_DELAY * Math.pow(2, retryCount), // exponential backoff
        );
      } else {
        // call original onError handler (if passed) when all retries exhausted
        onError?.(event);
      }
    },
    [retryCount, onError],
  );

  return <img {...props} key={imgKey} onError={handleError} />;
};

export const Image = (props: Props) => {
  // use key to reset internal state when src changes
  // img with different src should be treated as a new image
  return <ImageInternal {...props} key={props.src} />;
};
